import { Grid, IconButton, Stack, Tooltip, styled } from "@mui/material";
import { DownloadSimpleIcon } from "@phosphor-icons/react/dist/csr/DownloadSimple";
import { FileIcon } from "@phosphor-icons/react/dist/csr/File";
import { KeyIcon } from "@phosphor-icons/react/dist/csr/Key";
import { UploadSimpleIcon } from "@phosphor-icons/react/dist/csr/UploadSimple";
import type { ReactElement } from "react";
import React, { Fragment, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import DraftStatus from "../../constants/draftStatus";
import type { CompilationResult, Credential } from "../../graphql/types";
import DiffIcon from "../../icons/Diff.svg?react";
import { useEditorStoreContext } from "../../store/editor/EditorStoreProvider";
import { selectAllDeclarations } from "../../store/editor/editorStore";
import AssetPanel, { AssetPanelItem } from "./AssetPanel";
import DiffFilePanel from "./DiffFilePanel";
import FilePanel from "./FilePanel";

const PanelMenuItem = styled(IconButton, {
  shouldForwardProp: (prop) => prop !== "selected",
})<{ selected: boolean }>(({ selected, theme }) => {
  return {
    borderRadius: "6px",
    height: "60px",
    width: "80%",
    margin: theme.spacing(1),
    color: theme.palette.text30.main,
    ...(selected && {
      "& .icon": {
        color: theme.palette.text50.main,
      },
    }),
  };
});

const PanelMenu = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const currentMode = searchParams.get("mode");
  const isPublished = useEditorStoreContext((state) => state.status === DraftStatus.RUNNING);
  const modesIconsMap: Record<string, ReactElement> = {
    Files: <FileIcon fontSize="2.5rem" className="icon" />,
    Imports: <DownloadSimpleIcon fontSize="2.5rem" className="icon" />,
    Exports: <UploadSimpleIcon fontSize="2.5rem" className="icon" />,
    Keys: <KeyIcon fontSize="2.5rem" className="icon" />,
  };

  if (!isPublished)
    modesIconsMap["Changes"] = <DiffIcon fontSize="2.5rem" className="icon" data-testid="difference-outlined-icon" />;

  const handleSelectMode = (mode: string) => {
    setSearchParams((params) => {
      params.set("mode", mode);
      return params;
    });
  };

  return (
    <Stack sx={{ height: "100%", width: "100%", alignItems: "center" }}>
      {Object.keys(modesIconsMap).map((mode, idx) => {
        return (
          <Fragment key={idx}>
            <Tooltip title={mode} placement="right">
              <PanelMenuItem
                key={idx}
                onClick={() => handleSelectMode(mode)}
                selected={mode === currentMode}
                data-cy={`${mode}-menu-item`}
              >
                {modesIconsMap[mode]}
              </PanelMenuItem>
            </Tooltip>
          </Fragment>
        );
      })}
    </Stack>
  );
};

interface IAssetPanelController {
  keys?: Array<Credential>;
}

const createDeclarationAssetMap = ({
  type,
  declarations,
}: {
  type: string;
  declarations?: CompilationResult["declarations"];
}) => {
  if (!declarations) return {};
  const items = declarations.filter((d) => d.declaration_type === type);
  const assetMap: Record<string, AssetPanelItem[]> = {};

  for (const item of items) {
    const { connector_type, name, test } = item;
    if (Object.hasOwn(assetMap, connector_type)) {
      assetMap[connector_type].push({ name, testing: test });
    } else {
      assetMap[connector_type] = [{ name, testing: test }];
    }
  }

  return new Map<string, Array<AssetPanelItem>>(Object.entries(assetMap));
};

const createKeyAssetMap = ({
  keys,
  declarations,
}: {
  keys?: Array<Credential>;
  declarations?: CompilationResult["declarations"];
}) => {
  if (!keys || !declarations) return {};
  const assetMap: Record<string, AssetPanelItem[]> = {};
  for (const key of keys) {
    if (!key) continue;
    const { credential_type, name } = key;
    const parentDeclaration = declarations.find((d) => d.key_name === name);
    const parentAsset = parentDeclaration
      ? { name: parentDeclaration.name, declarationType: parentDeclaration.declaration_type as "import" | "export" }
      : undefined;
    if (Object.hasOwn(assetMap, credential_type)) {
      assetMap[credential_type].push({ name, parentAsset });
    } else {
      assetMap[credential_type] = [{ name, parentAsset }];
    }
  }
  return new Map<string, Array<AssetPanelItem>>(Object.entries(assetMap));
};

const AssetPanelController: React.FC<IAssetPanelController> = ({ keys }) => {
  const [searchParams] = useSearchParams();
  const declarations = useEditorStoreContext(selectAllDeclarations);
  const mode = searchParams.get("mode") || "Files";
  const changelistId = useEditorStoreContext((state) => state.id);
  const isPublished = useEditorStoreContext((state) => state.status === DraftStatus.RUNNING);

  if (mode === "Imports" || mode === "Exports") {
    const assetMap = createDeclarationAssetMap({
      type: mode === "Exports" ? "export" : "import",
      declarations,
    }) as Map<string, Array<AssetPanelItem>>;
    return <AssetPanel assetMap={assetMap} mode={mode} />;
  }

  if (mode === "Keys") {
    const assetMap = createKeyAssetMap({ keys, declarations }) as Map<string, Array<AssetPanelItem>>;
    return <AssetPanel assetMap={assetMap} mode={mode} />;
  } else if (mode === "Changes" && !isPublished) {
    return <DiffFilePanel modifiedId={changelistId} />;
  }

  return <FilePanel />;
};

const PanelContainer = styled(Grid)(({ theme }) => ({
  minHeight: "100%",
  borderRight: `0.5px solid ${theme.palette.surfaceDark30.main}`,
}));

const AssetPanelContainer = styled("div")({
  height: "calc(100vh - 50px)",
  width: "100%",
  overflow: "auto",
});

const EditorPanels = ({ keys }: { keys?: Array<Credential> }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const currentMode = searchParams.get("mode") || undefined;

  useEffect(() => {
    if (!currentMode)
      setSearchParams((params) => {
        params.set("mode", "Files");
        return params;
      });
  }, [currentMode, searchParams, setSearchParams]);

  return (
    <Grid container sx={{ minHeight: "100%", minWidth: "100%" }}>
      <PanelContainer container size={2}>
        <PanelMenu />
      </PanelContainer>
      <PanelContainer size={10} sx={{ flexGrow: 1 }}>
        <AssetPanelContainer>
          <AssetPanelController keys={keys} />
        </AssetPanelContainer>
      </PanelContainer>
    </Grid>
  );
};

export default EditorPanels;
